import { take, filter, catchError, of, pipe, Observable } from 'rxjs';

/**
 * @module Utils.Util
 */
function createUtil(_window) {
    const invalidCharacterRegex = /[^\w.-]/g;

    let _util = {};

    function isObject(value) {
        return value !== null && typeof value === 'object';
    }

    function isFunction(value) {
        return typeof value === 'function';
    }

    function isDate(value) {
        return _window.toString.call(value) === '[object Date]';
    }

    function isRegExp(value) {
        return _window.toString.call(value) === '[object RegExp]';
    }

    function removeProperty(key) {
        delete this[key];
    }

    /**
     * Merge a source object into a destination object in a way friendly to Vue
     * change tracking, but also works with other frameworks
     */
    _util.merge = function(destination, source) {
        if (!isObject(source) || isFunction(source)) {
            return;
        }
        Object.keys(source).forEach(key => {
            const value = source[key];
            if (isObject(value)) {
                const nodeType = value?.ownerDocument?.defaultView?.Node;
                if (isObject(nodeType) && value instanceof nodeType) {
                    // do not merge values that are Node, Element, HTMLElement, etc.
                    return;
                }
                if (isDate(value)) {
                    destination[key] = new _window.Date(value.valueOf());
                } else if (isRegExp(value)) {
                    destination[key] = new RegExp(value);
                } else {
                    if (!isObject(destination[key])) {
                        destination[key] = Array.isArray(value) ? [] : {};
                    }
                    _util.merge(destination[key], value);
                }
            } else {
                destination[key] = value;
            }
        });
        return destination;
    };

    _util.query = function() {
        const rawQuery = _window.location.search.substr(1).split('&');
        const _query = {};
        if (Array.isArray(rawQuery)) {
            rawQuery.forEach(function(item) {
                const p = item.split('=');
                if (p.length === 2) {
                    _query[p[0]] = decodeURIComponent(p[1].replace(/\+/g, ' '));
                }
            });
        }
        return _query;
    };

    _util.pixelRatio = (function() {
        return Math.max(1, Math.round(_window.devicePixelRatio));
    }());

    function getNativeMessageString(message, log) {
        try {
            if (typeof message === 'string') {
                return message;
            } else if (message === null || message === undefined) {
                return '';
            }
            return JSON.stringify(message);
        } catch (e) {
            log({
                code: 'nativeMethodInvalidMessage',
                error: e,
                traceId: message?.traceId
            });
        }
    }

    /**
     * @description For logging, do not include sensitive properties containing PII or auth info
     *     and do not include the traceId, which is logged elsewhere.
     */
    function messageForLog(message) {
        let result = {};
        if (typeof message === 'object') {
            Object.keys(message).forEach(key => {
                if (!'oauthToken,authToken,sessionId,seedonkSession,traceId'.toLowerCase().includes(key.toLowerCase())) {
                    result[key] = message[key];
                } else {
                    result[key] = '...';
                }
            });
        }
        return result;
    }

    /**
     * @param {String} method - name of the method to call on iOS native
     * @param {*} message - a value of any type that will be converted to string if needed and sent to native
     * @param {Function} log - a logging function
     * @returns {Boolean} true if the call was successful, false if not
     */
    _util.iosNativeMethod = function(method, message, log) {
        try {
            if (window?.webkit?.messageHandlers) {
                if (window.webkit.messageHandlers[method]) {
                    window.webkit.messageHandlers[method].postMessage(
                        getNativeMessageString(message, log)
                    );
                    log({
                        code: 'nativeMethod',
                        type: 'info',
                        object: {
                            f: method,
                            m: messageForLog(message)
                        },
                        traceId: message?.traceId
                    });
                    return true;
                }
            }
        } catch (e) {
            log({
                code: 'nativeMethod',
                type: 'issue',
                object: {
                    f: method,
                    m: messageForLog(message)
                },
                error: e,
                traceId: message?.traceId
            });
        }
        return false;
    };

    /**
     * @param {String} method - name of the method to call on Android native
     * @param {*} message - a value of any type to send to native
     * @param {Function} log - a logging function
     * @returns {Boolean} true if the call was successful, false if not
     */
    _util.androidNativeMethod = function(method, message, log) {
        try {
            if (window.SeedonkAndroid) {
                if (window.SeedonkAndroid[method]) {
                    window.SeedonkAndroid[method](getNativeMessageString(message, log));
                    log({
                        code: 'nativeMethod',
                        type: 'info',
                        object: {
                            f: method,
                            m: messageForLog(message)
                        },
                        traceId: message?.traceId
                    });
                    return true;
                }
            }
        } catch (e) {
            log({
                code: 'nativeMethod',
                type: 'issue',
                object: {
                    f: method,
                    m: messageForLog(message)
                },
                error: e,
                traceId: message?.traceId
            });
        }
        return false;
    };

    /**
     * @param {String} method - name of the method to call on native
     * @param {*} message - a value of any type to send to native
     * @param {Function} log - a logging function
     * @returns {Boolean} true if the call was successful, false if not
     */
    _util.nativeMethod = function(method, message, log) {
        return _util.androidNativeMethod(method, message, log)
            || _util.iosNativeMethod(method, message, log);
    };

    /**
     * @returns {Boolean} true if this web app is embedded inside a supported native app;
     *     false if the web app is running in a stand-alone browser
     */
    _util.isEmbedded = function() {
        return Boolean(_window.SeedonkAndroid
                || _window?.webkit?.messageHandlers
                || _util.query().appVersion);
    };

    _util.noop = function() {
        /* noop - no operation */
    };

    const endsWithGuid = new RegExp('[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');

    /**
     * @description If the device.ownership.ownerAlias is from myQ, it will have
     *     a prefix before the Guid value, such as "myq" or "ast". Clients are not expected
     *     to use such prefixes, but may. This function strips off any prefixes if the value
     *     is in the form of a Guid.
     * @param {String} text an owner alias value
     * @returns {String} ownerAlias with any Guid prefixes removed
     */
    _util.aliasWithNoGuidPrefix = function(text) {
        if (typeof text === 'string') {
            const matchGuid = text.match(endsWithGuid);
            if (matchGuid) {
                return matchGuid[0].toLowerCase();
            }
            return text.toLowerCase();
        }
    };

    _util.nonBreakingSpaceCharacter = String.fromCharCode(160);

    _util.base64 = (function() {
        // RFC 4648 section 5 - Base 64 Encoding with URL and Filename Safe Alphabet
        const base64url = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_=';

        const base64urlLookup = (function() {
            const a = new Uint8Array(128);
            let i = 0;
            while (i < base64url.length) {
                a[base64url.charCodeAt(i)] = i;
                i += 1;
            }
            a['='.charCodeAt(0)] = 0; // '=' should not contribute any data, so should be 0
            // this allows other base64 specs to be supported for the last two characters
            // https://en.wikipedia.org/wiki/Base64
            a['+'.charCodeAt(0)] = 62;
            a['/'.charCodeAt(0)] = 63;
            a[','.charCodeAt(0)] = 63;
            return a;
        }());

        /**
         * @param {Uint8Array} array
         * @param {Object} [options]
         * @param {Boolean} [options.padding] false to not use padding characters at the end
         * @returns {String} returns the array encoded as Base64 with URL and filename safe alphabet
         */
        function encodeUint8Array(array, options) {
            let byteIndex = 0; // the current index of the input array
            let result = '';
            let a = 0; // the most significant 6 bits of byte 0, 3, 6, etc.
            let b = 0; // the least significant 2 bits of byte 0, 3, 6, etc.
            let c = 0; // the most significant 4 bits of byte 1, 4, 7, etc.
            let d = 0; // the least significant 4 bits of byte 1, 4, 7, etc.
            let e = 0; // the most significant 2 bits of byte 2, 5, 8, etc.
            let f = 0; // the least significant 6 bits of byte 2, 5, 8, etc.
            let byteType = 0; // There are 3 kinds of bytes that are treated differently - 0, 1, and 2

            // loop through all bytes in the source, taking the relevant bits from each byte
            // and generating results when enough bits have been captured
            while (byteIndex < array.length) {
                if (byteType === 0) {
                    a = array[byteIndex] >> 2;
                    b = array[byteIndex] & 3;
                    result += base64url[a];
                    byteType = 1;
                } else if (byteType === 1) {
                    c = array[byteIndex] >> 4;
                    d = array[byteIndex] & 15;
                    result += base64url[((b << 4) + c)];
                    byteType = 2;
                } else if (byteType === 2) {
                    e = array[byteIndex] >> 6;
                    f = array[byteIndex] & 63;
                    result += base64url[((d << 2) + e)];
                    result += base64url[f];
                    byteType = 0;
                }
                byteIndex += 1;
            }

            // handle any leftover bits
            if (byteType === 1) {
                result += base64url[b << 4];
                if (!options || options.padding !== false) {
                    result += base64url[64]; // insert padding for missing bits
                    result += base64url[64]; // insert padding for missing bits
                }
            } else if (byteType === 2) {
                result += base64url[d << 2];
                if (!options || options?.padding !== false) {
                    result += base64url[64]; // insert padding for missing bits
                }
            }

            return result;
        }

        /**
         * @param {String} value
         * @returns {Uint8Array}
         */
        function decodeUint8Array(value) {
            if (typeof value !== 'string') {
                throw new Error('input must be a string');
            }

            // add missing pad characters
            let paddedValue = value;
            let missingPads = value.length % 4;
            if (missingPads === 3) {
                paddedValue += '=';
            } else if (missingPads === 2) {
                paddedValue += '==';
            } else if (missingPads === 1) {
                throw new Error('invalid base64 input');
            }

            // set the length of the result
            let pads = 0;
            if (paddedValue.charAt(paddedValue.length - 1) === '=') {
                pads += 1;
            }
            if (paddedValue.charAt(paddedValue.length - 2) === '=') {
                pads += 1;
            }
            let result = new Uint8Array(paddedValue.length * 0.75 - pads);

            // calculate the result
            let textIndex = 0;
            let arrayIndex = 0;
            let code0;
            let code1;
            let code2;
            let code3;
            while (textIndex < paddedValue.length) {
                code0 = base64urlLookup[paddedValue.charCodeAt(textIndex)];
                code1 = base64urlLookup[paddedValue.charCodeAt(textIndex + 1)];
                code2 = base64urlLookup[paddedValue.charCodeAt(textIndex + 2)];
                code3 = base64urlLookup[paddedValue.charCodeAt(textIndex + 3)];
                result[arrayIndex] = (code0 << 2) | (code1 >> 4);
                result[arrayIndex + 1] = ((code1 & 15) << 4) | (code2 >> 2);
                result[arrayIndex + 2] = ((code2 & 3) << 6) | (code3);
                arrayIndex += 3;
                textIndex += 4;
            }

            return result;
        }

        return {
            encodeUint8Array: encodeUint8Array,
            decodeUint8Array: decodeUint8Array
        };
    }());

    _util.submitForm = function(_window, form) {
        // Create a form and submit it
        const formElement = _window.document.createElement('form');

        // set the form element
        formElement.id = 'myform';
        formElement.name = 'myform';
        formElement.action = form.action;
        formElement.method = form.method;

        // this must be set for IE, or the form will show
        formElement.style.display = 'none';

        _window.document.body.appendChild(formElement);

        // add input elements from the values
        if (Array.isArray(form.values)) {
            form.values.forEach(function(value) {
                let inputElement = _window.document.createElement('input');
                inputElement.name = value.name;
                inputElement.value = value.value;
                formElement.appendChild(inputElement);
            });
        }

        formElement.submit();
    };

    /**
     * @param {Number} length The number of bytes to use for code generation,
     *     not the length of the string output. Multiples of 3 work best.
     * @returns {String} a Base64-encoded cryptographically secure random number as a string.
     *     Note that the length of the returned value will be longer than the length parameter,
     *     since Base64 uses 4 characters for every 3 bytes, not including padding.
     */
    _util.randomBase64Code = function(length) {
        const bytes = new Uint8Array(length);
        _window.crypto.getRandomValues(bytes);
        return _util.base64.encodeUint8Array(bytes);
    };

    _util.isValidVideoElement = function(el) {
        return el?.nodeName?.toLowerCase() === 'video';
    };

    _util.getTomorrowDate = function(dateValue) {
        const date = new _window.Date(dateValue);
        date.setDate(date.getDate() + 1);
        date.setSeconds(date.getSeconds() + 1);
        return date;
    };

    _util.getTendPaymentProcessorId = function() {
        return (_window.location.host.includes('dev.')
                || _window.location.host.includes('preprod.')
                || !_window.location.host.includes('.'))
            ? 'BraintreeSb'
            : 'Braintree';
    };

    _util.getKeepalive = function(streamObject, apiService, logTypeEnum) {
        return _window.setInterval(() => {
            apiService.keepalive({
                viewerSession: streamObject?.viewerSession,
                traceId: _util.randomBase64Code(15)
            }).then(
                () => streamObject?.info?.next({
                    type: logTypeEnum?.keepAliveConfirmed,
                    deviceId: streamObject?.deviceId
                }),
                e => streamObject?.info?.next({
                    type: logTypeEnum?.keepAliveFailed,
                    deviceId: streamObject?.deviceId,
                    error: e
                })
            );
        }, 7000);
    };

    _util.createMediaSession = function(streamObject, apiService, logTypeEnum) {
        return new Observable(subscriber => {
            apiService.devices.createMediaSession({
                deviceId: streamObject?.deviceId,
                keepaliveSec: 10,
                traceId: streamObject?.traceId
            }).then(
                viewerSessionResult => {
                    if (streamObject) {
                        streamObject.viewerSession
                            = viewerSessionResult.data.viewerSessionId;
                        streamObject.info.next({
                            type: logTypeEnum.viewerSessionCreated,
                            deviceId: streamObject?.deviceId
                        });
                    }
                    subscriber.next(viewerSessionResult.data.viewerSessionId);
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    _util.startMedia = function(streamObject, apiService, logTypeEnum, log, streamType) {
        apiService.startMedia({
            deviceId: streamObject?.deviceId,
            p2p: false,
            type: streamType, // 'AUDIO' or 'VIDEO'
            sessionId: streamObject?.viewerSession,
            keepaliveSec: 10,
            traceId: streamObject?.traceId
        }).then(
            () => streamObject?.info.next({
                type: logTypeEnum.streamStartConfirmed,
                deviceId: streamObject?.deviceId,
                streamType: streamType
            }),
            e => {
                streamObject?.info.next({
                    type: logTypeEnum.streamStartFailed,
                    deviceId: streamObject?.deviceId,
                    streamType: streamType
                });
                log({
                    code: 'startMediaFailed',
                    deviceId: streamObject?.deviceId,
                    traceId: streamObject?.traceId,
                    object: { streamType: streamType },
                    error: e
                });
            }
        );
        streamObject?.info.next({
            type: logTypeEnum.streamStartRequested,
            deviceId: streamObject?.deviceId,
            streamType: streamType
        });
    };

    /**
    * @description
    * 1) make sure the value exists
    * 2) only take the first one to complete the Observable
    */
    _util.takeFirstExisting = pipe(
        filter(item => item !== undefined),
        take(1)
    );

    /**
    * @description
    * 1) make sure the value exists
    * 2) only take the first one to complete the Observable
    * 3) catch any errors to log later
    */
    _util.takeFirstExistingWithNoError = pipe(
        filter(item => item !== undefined),
        take(1),
        catchError(e => of({isError: true, error: e}))
    );

    /**
     * @param {Object|undefined} obj
     * @returns {Object} An object with a traceId property that is a copy of obj.
     *     If the obj param is falsy, a new object will be created.
     *     If obj already had a traceId, it is copied to the returned result unaltered.
     */
    _util.withTraceId = function(obj) {
        if (typeof obj !== 'object' && typeof obj !== 'undefined') {
            throw new Error('util.withTraceId only accepts an object or undefined');
        }
        const newObj = _util.merge({}, obj ?? {});
        newObj.traceId ??= _util.randomBase64Code(15);
        return newObj;
    };

    /**
     * @description For Arrays, removes all the items. For other Objects, removes all properties
     *     returned by Object.keys()
     * @param {Object} obj Any object or Array
     */
    _util.clear = function(obj) {
        if (Array.isArray(obj)) {
            obj.splice(0);
        } else if (typeof obj === 'object') {
            Object.keys(obj).forEach(removeProperty, obj);
        }
    };

    /**
     * @description lambda function that matches by "id" property
     */
    _util.byId = function(o) {
        return o.id === this.id;
    };

    /**
     * @param {String} value
     * @returns {String}
     */
    _util.removeInvalidCharacters = function(value) {
        if (typeof value === 'string' && value !== '') {
            return value.replace(invalidCharacterRegex, '');
        }
        return '';
    };

    /**
     * @param {Number} value
     * @returns {String} A string interpretation of a MediaError code
     */
    _util.mediaErrorCode = function(value) {
        if (value === _window.MediaError.MEDIA_ERR_ABORTED) {
            return 'MEDIA_ERR_ABORTED';
        }
        if (value === _window.MediaError.MEDIA_ERR_NETWORK) {
            return 'MEDIA_ERR_NETWORK';
        }
        if (value === _window.MediaError.MEDIA_ERR_DECODE) {
            return 'MEDIA_ERR_DECODE';
        }
        if (value === _window.MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
            return 'MEDIA_ERR_SRC_NOT_SUPPORTED';
        }
        return '' + value;
    };

    /**
     * @param {ArrayBuffer} arrayBuffer
     * @param {String} [type='image/jpeg'] The optional MIME type of the image data.
     *     Defaults to 'image/jpeg', but could be 'image/png' or 'image/gif' or more.
     * @returns {Observable<HTMLImage>}
     */
    _util.arrayBufferToImage = function(arrayBuffer, type) {
        return new Observable(subscriber => {
            if (!(arrayBuffer instanceof ArrayBuffer)) {
                subscriber.error(new Error('arrayBufferToImage param is not an ArrayBuffer'));
            }
            if (type !== undefined && (typeof type !== 'string')) {
                subscriber.error(new Error('arrayBufferToImage type must be undefined or MIME string'));
            }
            try {
                const arrayBufferView = new Uint8Array(arrayBuffer);
                const blob = new Blob( [ arrayBufferView ], { type: (type ?? 'image/jpeg') } );
                const urlCreator = _window.URL ?? _window.webkitURL;
                const imageUrl = urlCreator.createObjectURL( blob );
                const image = new _window.Image();
                image.onload = function() {
                    subscriber.next(image);
                    subscriber.complete();
                };
                image.src = imageUrl;
            } catch (e) {
                subscriber.error(e);
            }
        });
    };

    return _util;
}

export default createUtil;
